import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../features/auth/authSlice";
import Select from 'react-select';
import { customDropdownStyles } from "../utils/globalStyles";

const SuggestionModal = ({ isOpen, onClose, dir }) => {

  const { t } = useTranslation();

  const Category = {
    WORD_IMPROVEMENT: t("WORD_IMPROVEMENT"),
    OVERLAPPING_CONTENT: t("OVERLAPPING_CONTENT"),
    MISSING_CONTENT: t("MISSING_CONTENT"),
    TOPIC: t("TOPIC"),
    CONTRADICTION_IN_POLICIES: t("CONTRADICTION_IN_POLICIES"),
    COMPANY_LEVEL_CONCERN: t("COMPANY_LEVEL_CONCERN"),
    DEPARTMENT_LEVEL_CONCERN: t("DEPARTMENT_LEVEL_CONCERN"),
  };

  const profileData = JSON.parse(localStorage.getItem("profileData") || "{}");
  const companyName = profileData?.company?.companyName || "N/A";
  const departmentName = profileData?.department?.departmentName || "N/A";

  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "",
    company: companyName,
    department: departmentName,
    document: ""
    // priority: "Medium",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [subjectError, setSubjectError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [documenttList, setDocumentList] = useState([]);
  const containerRef = useRef(null);
  const dispatch = useDispatch()
  const navigate = useNavigate();

  const SUBJECT_MAX_LENGTH = 60;
  const DESCRIPTION_MAX_LENGTH = 500; // Added description character limit

  // Handle scroll to show/hide scroll indicator
  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 20;
      setShowScrollIndicator(!isAtBottom && scrollTop > 10);
    }
  };

  // Scroll to bottom when arrow is clicked
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleSignout = useCallback(() => {
    
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("profileData");
    
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("profileData");

    sessionStorage.removeItem("conversation");
    sessionStorage.removeItem("currentSessionId");
    sessionStorage.removeItem("sessionNumber");
    sessionStorage.removeItem("sessions");
    dispatch(logout())
    navigate("/login");
  }, [navigate, dispatch])

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PORTAL_APP_BASE_URL}/admin/documents/user-document?limit=200000`, {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          token: `${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          handleSignout();
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const documents = data.data;
      setDocumentList(documents)
      console.log(documents, "check document suggestion Modal");
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [handleSignout]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments])

  useEffect(() => {
    if (isOpen) {
      setFormData({
        subject: "",
        description: "",
        category: Category.WORD_IMPROVEMENT,
        company: companyName,
        department: departmentName,
        document: ""
      });
      setSuccess(false);
      setError(null);
      setSubjectError("");
      setDescriptionError("");
    }

  }, [isOpen, Category.WORD_IMPROVEMENT, companyName, departmentName]);

  // Add scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "subject") {
      // Clear the subject error when user starts typing again
      setSubjectError("");

      // Only update the subject if it's within the character limit
      if (value.length <= SUBJECT_MAX_LENGTH) {
        setFormData({
          ...formData,
          [name]: value.replace(/\s{2,}/g, " "),
        });
      } else {
        // If exceeding the limit, keep the previous value and show error
        setSubjectError(
          `Subject cannot exceed ${SUBJECT_MAX_LENGTH} characters.`
        );
      }
    } else if (name === "description") {
      // Clear the description error when user starts typing again
      setDescriptionError("");

      // Only update the description if it's within the character limit
      if (value.length <= DESCRIPTION_MAX_LENGTH) {
        setFormData({
          ...formData,
          [name]: value.replace(/\s{2,}/g, " "),
        });
      } else {
        // If exceeding the limit, keep the previous value and show error
        setDescriptionError(
          `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`
        );
      }
    } else {
      // For other fields, update normally
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate subject length before submission
    if (formData.subject.length > SUBJECT_MAX_LENGTH) {
      setSubjectError(
        `Subject cannot exceed ${SUBJECT_MAX_LENGTH} characters.`
      );
      return;
    }

    // Validate description length before submission
    if (formData.description.length > DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(
        `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`
      );
      return;
    }

    setLoading(true);
    setError(null);
    console.log(formData, "check Submit value");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PORTAL_APP_BASE_URL}/admin/support/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: `${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      setSuccess(true);
      setFormData({
        subject: "",
        description: "",
        category: "",
      });

      setSubjectError(null);
      setDescriptionError(null);
      // setTimeout(() => {
      //   setSuccess(false);
      //   onClose();
      // }, 1000);
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      setError(t("failed_to_suggestion"));
    } finally {
      setLoading(false);
    }
  };

  const documentOptions = documenttList.map((document) => ({
    value: document.id,
    label: document.documentTitle,
  }));

  const categoryOptions = Object.entries(Category).map(([key, value]) => ({
    value: value,
    label: value,
  }));

  console.log(documentOptions, categoryOptions);

  if (!isOpen) return null;

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000000000]">
      {/* Increased container size */}
      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg p-6 w-full overflow-y-auto relative"
        style={{
          maxWidth: "650px",
          maxHeight: "80vh", // Increased height
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {t("suggestion_to_the_management")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {success ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            <strong className="font-bold">{t("success")}</strong>
            <span className="block sm:inline">
              {" "}
              {t("success_desc")}
              {" "}
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                <strong className="font-bold">{t("error")}</strong>
                <span className="block sm:inline"> {error}</span>
              </div>
            )}
            <div className="mb-4 grid grid-cols-2 gap-4">
              {/* Company */}
              <div>
                <label
                  htmlFor="company"
                  className="block text-gray-700 font-medium mb-2"
                >
                  {t("company")}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  disabled
                  className={`w-full px-3 py-2 border ${subjectError ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]`}
                />
              </div>

              {/* Department */}
              <div>
                <label
                  htmlFor="department"
                  className="block text-gray-700 font-medium mb-2"
                >
                  {t("department")}
                </label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                  disabled
                  className={`w-full px-3 py-2 border ${subjectError ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]`}
                />
              </div>
            </div>
            <div className="mb-4">
              <label
                htmlFor="document"
                className="block text-gray-700 font-medium mb-2"
              >
                {t("document")}
              </label>
              {/* <select
                id="document"
                name="document"
                value={formData.document}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]"
              >
                <option value="">{t("select_document")}</option>
                {documenttList.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.documentTitle}
                  </option>
                ))}
              </select> */}
              <Select
                className="text-md z-[10000]"
                classNamePrefix="react-select"
                options={documentOptions}
                value={documentOptions.find((opt) => opt.value === formData.document)}
                onChange={(selectedOption) => setFormData({ ...formData, document: selectedOption?.value || '' })}
                isSearchable
                placeholder={t("Select Company Ownership Structure")}
                isClearable
                styles={customDropdownStyles}
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="category"
                className="block text-gray-700 font-medium mb-2"
              >
                {t("category")}
              </label>
              {/* <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]"
              >
                {Object.entries(Category).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select> */}
              <Select
                className="text-md"
                classNamePrefix="react-select"
                options={categoryOptions}
                value={categoryOptions.find((opt) => opt.value === formData.category)}
                onChange={(selectedOption) => setFormData({ ...formData, category: selectedOption?.value || '' })}
                isSearchable
                placeholder={t("Select Category")}
                isClearable
                styles={customDropdownStyles}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="subject"
                className="block text-gray-700 font-medium mb-2"
              >
                {t("subject")}
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                maxLength={SUBJECT_MAX_LENGTH}
                className={`w-full px-3 py-2 border ${subjectError ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]`}
                placeholder={t("Enter_the_subject_of_your_suggestion")}
              />
              {subjectError && (
                <p className="text-red-500 text-sm mt-1">{subjectError}</p>
              )}
              <div className="text-sm text-gray-500 mt-1">
                {formData.subject.length}/{SUBJECT_MAX_LENGTH} {t("characters")}
              </div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="description"
                className="block text-gray-700 font-medium mb-2"
              >
                {t("description")}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="6" // Increased rows
                maxLength={DESCRIPTION_MAX_LENGTH}
                className={`w-full px-3 py-2 border ${descriptionError ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]`}
                placeholder={t("description_your_suggestion")}
              ></textarea>
              {descriptionError && (
                <p className="text-red-500 text-sm mt-1">{descriptionError}</p>
              )}
              <div className="text-sm text-gray-500 mt-1">
                {formData.description.length}/{DESCRIPTION_MAX_LENGTH}{" "}
                {t("characters")}
              </div>
            </div>


            {/* <div className="mb-4">
              <label
                htmlFor="priority"
                className="block text-gray-700 font-medium mb-2"
              >
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B03982]"
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div> */}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 mr-2"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={loading || subjectError || descriptionError}
                className={`px-4 py-2 rounded-md text-white font-medium ${loading || subjectError || descriptionError
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#B03982] hover:bg-[#8c2d66]"
                  }`}
              >
                {loading ? t("submitting") : t("submit_suggestion")}
              </button>
            </div>
          </form>
        )}

        {/* Scroll down indicator positioned on right side */}
        {showScrollIndicator && (
          <button
            onClick={scrollToBottom}
            className="absolute right-6 bottom-24 bg-white hover:bg-gray-100 rounded-full p-2 shadow-md z-10"
            aria-label="Scroll to bottom"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default SuggestionModal;
